// src/pages/orders/AddOrder.js
import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  FiPlus,
  FiTrash2,
  FiShoppingCart,
  FiUser,
  FiUserCheck,
  FiPackage,
  FiPercent,
  FiCalendar,
  FiChevronLeft,
  FiSearch,
} from "react-icons/fi";
import API_BASE_URL from "../../utils/config";
import Select from "react-select";

/* ---------------- helpers ---------------- */
const STATUS_COLORS = { OK: "#22c55e", ONLY_EXPIRED: "#f59e0b", NO_STOCK: "#ef4444" };
const inferStatus = (p) => {
  const avail = Number(p.available_stock ?? p.stock_available ?? 0);
  const total = Number(p.total_stock ?? p.stock_total ?? p.current_stock ?? avail);
  if (total <= 0) return { code: "NO_STOCK", color: STATUS_COLORS.NO_STOCK };
  if (avail <= 0) return { code: "ONLY_EXPIRED", color: STATUS_COLORS.ONLY_EXPIRED };
  return { code: "OK", color: STATUS_COLORS.OK };
};
const toProductOptions = (products) =>
  products.map((p) => ({
    value: p.id,
    label: p.name,
    product: p,
    status: inferStatus(p),
    mrp: Number(p.latest_mrp ?? p.mrp ?? 0),
    gst: Number(p.tax_percent ?? 0),
    isDisabled: inferStatus(p).code === "NO_STOCK",
  }));

const Dot = ({ color }) => (
  <span style={{ backgroundColor: color }} className="inline-block h-2.5 w-2.5 rounded-full mr-2 align-middle" />
);

const ProductOption = ({ innerProps, innerRef, isFocused, data }) => {
  const { label, status, product, mrp } = data;
  const avail = Number(product.available_stock ?? product.stock_available ?? 0);
  return (
    <div ref={innerRef} {...innerProps} className={`px-3 py-2 text-sm ${isFocused ? "bg-gray-100" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center min-w-0">
          <Dot color={status.color} />
          <span className="truncate">{label}</span>
        </div>
        <div className="ml-3 text-xs text-gray-500 whitespace-nowrap">
          {avail} in stock • MRP ₹{mrp.toFixed(2)}
        </div>
      </div>
    </div>
  );
};

const ProductSingleValue = ({ data }) => (
  <div className="flex items-center">
    <Dot color={data.status.color} />
    <span className="truncate">{data.label}</span>
  </div>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 12,
    borderColor: state.isFocused ? "#3b82f6" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(59,130,246,0.2)" : "none",
    minHeight: 40,
  }),
  menu: (base) => ({ ...base, borderRadius: 12, overflow: "hidden", zIndex: 40 }),
};

const toNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
const round2 = (n) => Math.round(toNum(n) * 100) / 100;
const fmtINR = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(toNum(n));
const rows = (d) => (Array.isArray(d) ? d : Array.isArray(d?.rows) ? d.rows : []); // always array

/* ---------------- component ---------------- */
export default function AddOrder() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const queryParams = new URLSearchParams(location.search);
  const customerIdFromQuery = queryParams.get("customerId") || "";

  // customers (paged + search)
  const [customers, setCustomers] = useState([]);
  
  const [custPage, setCustPage] = useState(1);
  const [custLimit, setCustLimit] = useState(50);
  const [custTotal, setCustTotal] = useState(0);
  const [customerQuery, setCustomerQuery] = useState(""); // server-side q
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // doctors/products
  const [doctors, setDoctors] = useState([]);
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  // items + form
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    customer_id: customerIdFromQuery,
    doctor_id: "",
  });

  // ---- Load master data ----
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);

        // customers now support page/limit/q and return {page,limit,total,rows}
        const [c, d, p] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/customers`, { ...auth, params: { page: custPage, limit: custLimit, q: customerQuery } }),
          axios.get(`${API_BASE_URL}/api/doctors`, auth),
          axios.get(`${API_BASE_URL}/api/products/forOrder`, auth),
        ]);
        if (!mounted) return;

        // customers
        const cPayload = c.data;
        if (cPayload && Array.isArray(cPayload.rows)) {
          setCustomers(cPayload.rows);
          setCustPage(cPayload.page ?? custPage);
          setCustLimit(cPayload.limit ?? custLimit);
          setCustTotal(cPayload.total ?? cPayload.rows.length);
        } else {
          // fallback in case server sends array
          const arr = rows(cPayload);
          setCustomers(arr);
          setCustPage(1);
          setCustLimit(arr.length);
          setCustTotal(arr.length);
        }

        // doctors
        setDoctors(rows(d.data));

        // products normalized
        const prods = rows(p.data).map((x) => ({
          id: x.product_id,
          name: x.product_name,
          tax_percent: toNum(x.gst_percent),
          mrp: toNum(x.latest_mrp ?? x.mrp),
          available_stock: toNum(x.available_stock),
          total_stock: toNum(x.total_stock),
          stock_status: x.stock_status,
          stock_color: x.stock_color,
          rate_excl_gst_margin20: toNum(x.rate_excl_gst_margin20),
          rate_incl_gst_margin20: toNum(x.rate_incl_gst_margin20),
        }));
        setProducts(prods);
        setProductOptions(toProductOptions(prods));
      } catch (e) {
        console.error(e);
        alert("Failed to load master data.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]); // initial load only

  // ---- Refetch customers when search or paging changes ----
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        setLoadingCustomers(true);
        const r = await axios.get(`${API_BASE_URL}/api/customers`, {
          ...auth,
          params: { page: custPage, limit: custLimit, q: customerQuery },
        });
        if (abort) return;
        const payload = r.data;
        if (payload && Array.isArray(payload.rows)) {
          setCustomers(payload.rows);
          setCustTotal(payload.total ?? payload.rows.length);
        } else {
          const arr = rows(payload);
          setCustomers(arr);
          setCustTotal(arr.length);
        }
      } catch (e) {
        if (!abort) {
          console.error("customers fetch error", e);
          setCustomers([]);
          setCustTotal(0);
        }
      } finally {
        if (!abort) setLoadingCustomers(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [auth, custPage, custLimit, customerQuery]);

  /* ---------- form handlers ---------- */
  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const addItem = () =>
    setOrderItems((prev) => [
      ...prev,
      {
        product_id: "",
        mrp: 0,
        quantity: 1,
        scheme_percent: 0,
        scheme_quantity: 0,
        unit_price: 0,
        margin: 20,
        discount: 0,
        gst: 0,
        expiry_date: "",
        total_price: 0,
      },
    ]);

  const removeItem = (index) => setOrderItems((prev) => prev.filter((_, i) => i !== index));

  const recalcItem = (item) => {
    const baseRate = toNum(item.unit_price);
    const discountAmt = (baseRate * toNum(item.discount)) / 100;
    const taxable = baseRate - discountAmt;
    const gstAmt = (taxable * toNum(item.gst)) / 100;
    const lineTotal = round2((taxable + gstAmt) * toNum(item.quantity, 1));
    return { ...item, total_price: lineTotal };
  };

  const recomputeScheme = (item) => {
    const qty = Math.max(1, toNum(item.quantity, 1));
    const sp = Math.min(100, Math.max(0, toNum(item.scheme_percent, 0)));
    const free = Math.floor((qty * sp) / 100);
    return { ...item, scheme_percent: sp, scheme_quantity: free };
  };

  const updateItem = async (index, key, value) => {
    const newItems = [...orderItems];
    let item = { ...newItems[index] };

    if (key === "product_id") {
      const prod = products.find((p) => p.id === Number(value));
      if (prod) {
        const mrp = toNum(prod.mrp);
        const gst = toNum(prod.tax_percent);
        const margin = toNum(item.margin, 20);
        const priceExclGST =
          margin === 20 && toNum(prod.rate_excl_gst_margin20)
            ? toNum(prod.rate_excl_gst_margin20)
            : round2((mrp / (1 + gst / 100)) * (1 - margin / 100));

        item.product_id = prod.id;
        item.mrp = mrp;
        item.gst = gst;
        item.unit_price = priceExclGST;

        try {
          const res = await axios.get(`${API_BASE_URL}/api/products/${prod.id}/batches?in_stock=1&limit=1`, auth);
          const dt = res?.data?.[0]?.expiry_date;
          if (dt) item.expiry_date = new Date(dt).toISOString().slice(0, 10);
        } catch (_) {}
      } else {
        item.product_id = "";
      }
    } else if (key === "margin") {
      item.margin = toNum(value, 0);
      item.unit_price = round2((toNum(item.mrp) / (1 + toNum(item.gst) / 100)) * (1 - toNum(item.margin) / 100));
    } else if (key === "unit_price") {
      item.unit_price = round2(value);
    } else if (key === "quantity") {
      item.quantity = Math.max(1, parseInt(value || 1, 10));
      item = recomputeScheme(item);
    } else if (key === "scheme_percent") {
      item.scheme_percent = toNum(value, 0);
      item = recomputeScheme(item);
    } else if (key === "discount") {
      item.discount = toNum(value, 0);
    }

    item = recalcItem(item);
    newItems[index] = item;
    setOrderItems(newItems);
  };

  const calculateTotals = () => {
    let subtotal = 0,
      totalGST = 0,
      total = 0,
      totalDiscount = 0;

    orderItems.forEach((item) => {
      const baseRate = toNum(item.unit_price);
      const discountAmt = (baseRate * toNum(item.discount)) / 100;
      const gstAmt = ((baseRate - discountAmt) * toNum(item.gst)) / 100;

      subtotal += (baseRate - discountAmt) * toNum(item.quantity, 1);
      totalGST += gstAmt * toNum(item.quantity, 1);
      totalDiscount += discountAmt * toNum(item.quantity, 1);
      total += (baseRate - discountAmt + gstAmt) * toNum(item.quantity, 1);
    });

    return { subtotal: round2(subtotal), totalGST: round2(totalGST), totalDiscount: round2(totalDiscount), total: round2(total) };
  };

  const { subtotal, totalGST, totalDiscount, total } = calculateTotals();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) return alert("Select customer");
    if (!orderItems.length) return alert("Add at least one product");

    try {
      const orderRes = await axios.post(`${API_BASE_URL}/api/orders`, formData, auth);
      const orderId = orderRes.data.id;
      await axios.post(`${API_BASE_URL}/api/orderItems/${orderId}/items`, orderItems, auth);
      alert("Order created successfully");
      navigate(`/customers/${formData.customer_id}/orders`);
    } catch (err) {
      console.error("Add order error:", err);
      alert("Failed to create order");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 pb-28">
      {/* Top bar */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/orders" className="inline-flex items-center gap-1 text-white/90 hover:text-white">
              <FiChevronLeft />
              Back
            </Link>
            <div className="flex items-center gap-2 font-semibold text-lg">
              <FiShoppingCart className="opacity-90" />
              Add New Order
            </div>
          </div>
          <div className="text-white/90 text-sm">
            {orderItems.length} item{orderItems.length !== 1 ? "s" : ""} • {fmtINR(total)}
          </div>
        </div>
      </div>

      {/* Customer & Doctor */}
      <div className="mt-6 bg-white rounded-2xl shadow-sm border p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiUser className="text-blue-600" />
          <h3 className="font-semibold text-lg">Customer & Doctor</h3>
        </div>

        {/* Customer search + paging */}
        {!customerIdFromQuery && (
          <div className="mb-3 bg-slate-50 border rounded-xl p-3">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="text-xs text-slate-600 mb-1 block">Search Customers</label>
                <div className="flex items-center gap-2 border rounded-xl px-3 py-2 bg-white">
                  <FiSearch className="text-slate-400" />
                  <input
                    value={customerQuery}
                    onChange={(e) => {
                      setCustPage(1);
                      setCustomerQuery(e.target.value);
                    }}
                    placeholder="Type name / phone / GSTIN"
                    className="w-full outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Page Size</label>
                <select
                  value={custLimit}
                  onChange={(e) => {
                    setCustPage(1);
                    setCustLimit(Number(e.target.value));
                  }}
                  className="border rounded px-3 py-2 bg-white"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-xs text-slate-600">
                {loadingCustomers ? "Loading…" : `${customers.length} / ${custTotal} shown`}
              </div>
            </div>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                disabled={custPage <= 1}
                onClick={() => setCustPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 rounded border bg-white disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-slate-600">Page {custPage}</span>
              <button
                type="button"
                disabled={custPage * custLimit >= custTotal}
                onClick={() => setCustPage((p) => p + 1)}
                className="px-3 py-1.5 rounded border bg-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600 mb-1 block">Customer</label>
            <select
              name="customer_id"
              value={formData.customer_id}
              onChange={handleChange}
              className={`w-full border rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                customerIdFromQuery ? "bg-gray-100 cursor-not-allowed" : ""
              }`}
              disabled={!!customerIdFromQuery}
            >
              <option value="">Select Customer</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.type ? ` (${c.type})` : ""}{" "}
                  {typeof c.outstanding === "number" ? `• ₹${Number(c.outstanding).toLocaleString("en-IN")}` : ""}
                </option>
              ))}
            </select>
            {customerIdFromQuery && (
              <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
                <FiUserCheck /> Pre-selected from customer page
              </div>
            )}
          </div>

          <div>
            <label className="text-sm text-gray-600 mb-1 block">Doctor</label>
            <select
              name="doctor_id"
              value={formData.doctor_id}
              onChange={handleChange}
              className="w-full border rounded-xl px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Doctor</option>
              {doctors.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Products block */}
      <div className="mt-6 bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FiPackage className="text-blue-600" />
            <h3 className="font-semibold text-lg">Products</h3>
          </div>
          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-green-600 text-white hover:bg-green-700"
          >
            <FiPlus /> Add Product
          </button>
        </div>

        {/* Header row (desktop only) */}
        {orderItems.length > 0 && (
          <div className="hidden md:grid grid-cols-12 gap-2 px-4 md:px-6 py-2 text-xs font-semibold uppercase text-gray-500 bg-gray-50">
            <div className="col-span-1">#</div>
            <div className="col-span-3">Product</div>
            <div className="col-span-1">MRP</div>
            <div className="col-span-1">Qty</div>
            <div className="col-span-1">Scheme %</div>
            <div className="col-span-1">Rate</div>
            <div className="col-span-1">Margin %</div>
            <div className="col-span-1">Disc %</div>
            <div className="col-span-1">GST %</div>
            <div className="col-span-1">Expiry</div>
            <div className="col-span-1 text-right">Line Total</div>
          </div>
        )}

        <div className="divide-y">
          {orderItems.map((item, idx) => {
            const totalUnits = toNum(item.quantity, 1) + toNum(item.scheme_quantity, 0);
            return (
              <div key={idx} className="px-4 md:px-6 py-3 hover:bg-gray-50">
                {/* DESKTOP ROW */}
                <div className="hidden md:grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-1 text-sm text-gray-500">{idx + 1}</div>
                  <div className="col-span-3">
                    <Select
                      options={productOptions}
                      value={productOptions.find((o) => o.value === item.product_id) || null}
                      onChange={(opt) => updateItem(idx, "product_id", opt?.value ?? "")}
                      isClearable
                      placeholder="Select product..."
                      styles={selectStyles}
                      components={{ Option: ProductOption, SingleValue: ProductSingleValue }}
                      menuPortalTarget={document.body}
                    />
                  </div>
                  <input type="number" value={item.mrp} readOnly className="col-span-1 border rounded-lg px-2 py-2 bg-gray-100" />
                  <div className="col-span-1">
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                      className="w-full border rounded-lg px-2 py-2"
                    />
                    <div className="text-[11px] text-gray-500 mt-1">
                      Free: <b>{item.scheme_quantity}</b> • Total: <b>{totalUnits}</b>
                    </div>
                  </div>
                  <div className="col-span-1 flex items-center gap-1">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={item.scheme_percent}
                      onChange={(e) => updateItem(idx, "scheme_percent", e.target.value)}
                      className="w-full border rounded-lg px-2 py-2"
                    />
                    <FiPercent className="text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={item.unit_price}
                    onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                    className="col-span-1 border rounded-lg px-2 py-2"
                  />
                  <div className="col-span-1 flex items-center gap-1">
                    <input
                      type="number"
                      value={item.margin}
                      onChange={(e) => updateItem(idx, "margin", e.target.value)}
                      className="w-full border rounded-lg px-2 py-2"
                    />
                    <FiPercent className="text-gray-400" />
                  </div>
                  <input
                    type="number"
                    value={item.discount}
                    onChange={(e) => updateItem(idx, "discount", e.target.value)}
                    className="col-span-1 border rounded-lg px-2 py-2"
                  />
                  <input type="number" value={item.gst} readOnly className="col-span-1 border rounded-lg px-2 py-2 bg-gray-100" />
                  <div className="col-span-1 flex items-center">
                    <FiCalendar className="text-gray-400 mr-2" />
                    <input type="date" value={item.expiry_date} readOnly className="w-full border rounded-lg px-2 py-2 bg-gray-100" />
                  </div>
                  <div className="col-span-1 text-right font-semibold">{fmtINR(item.total_price)}</div>
                  <div className="col-span-12 flex justify-end mt-2">
                    <button
                      onClick={() => removeItem(idx)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                </div>

                {/* MOBILE CARD */}
                <div className="md:hidden rounded-xl border p-3 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-slate-500">#{idx + 1}</div>
                    <button
                      onClick={() => removeItem(idx)}
                      className="inline-flex items-center gap-2 px-2 py-1 rounded-lg border text-red-600 border-red-200"
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>

                  {/* Product */}
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Product</label>
                    <Select
                      options={productOptions}
                      value={productOptions.find((o) => o.value === item.product_id) || null}
                      onChange={(opt) => updateItem(idx, "product_id", opt?.value ?? "")}
                      isClearable
                      placeholder="Select product..."
                      styles={selectStyles}
                      components={{ Option: ProductOption, SingleValue: ProductSingleValue }}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  {/* Qty + Scheme% */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Qty</label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min={1}
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, "quantity", e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                      <div className="text-[11px] text-gray-500 mt-1">
                        Free: <b>{item.scheme_quantity}</b> • Total: <b>{totalUnits}</b>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Scheme %</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          inputMode="decimal"
                          min={0}
                          max={100}
                          value={item.scheme_percent}
                          onChange={(e) => updateItem(idx, "scheme_percent", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <FiPercent className="text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Rate + Margin% */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Rate</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={item.unit_price}
                        onChange={(e) => updateItem(idx, "unit_price", e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Margin %</label>
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          inputMode="decimal"
                          value={item.margin}
                          onChange={(e) => updateItem(idx, "margin", e.target.value)}
                          className="w-full border rounded-lg px-3 py-2"
                        />
                        <FiPercent className="text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Disc% + GST% */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Disc %</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={item.discount}
                        onChange={(e) => updateItem(idx, "discount", e.target.value)}
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">GST %</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={item.gst}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                      />
                    </div>
                  </div>

                  {/* MRP + Expiry */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">MRP</label>
                      <input
                        type="number"
                        inputMode="decimal"
                        value={item.mrp}
                        readOnly
                        className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-500 mb-1 block">Expiry</label>
                      <div className="flex items-center">
                        <FiCalendar className="text-gray-400 mr-2" />
                        <input
                          type="date"
                          value={item.expiry_date}
                          readOnly
                          className="w-full border rounded-lg px-3 py-2 bg-gray-100"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Line total */}
                  <div className="text-right font-semibold">{fmtINR(item.total_price)}</div>
                </div>
              </div>
            );
          })}

          {!orderItems.length && !loading && (
            <div className="px-6 py-10 text-center text-gray-500">
              No items yet. Click{" "}
              <button onClick={addItem} className="inline-flex items-center gap-1 text-green-700 font-medium hover:underline">
                <FiPlus /> Add Product
              </button>{" "}
              to get started.
            </div>
          )}

          {loading && <div className="px-6 py-10 text-center text-gray-500">Loading…</div>}
        </div>
      </div>

      {/* Summary */}
      <div className="mt-6 bg-white rounded-2xl shadow-sm border p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <FiPercent className="text-blue-600" />
          <h3 className="font-semibold text-lg">Summary</h3>
        </div>
        <div className="grid md:grid-cols-4 gap-4 text-sm">
          <div className="rounded-xl border p-3">
            <div className="text-gray-500">Subtotal</div>
            <div className="font-semibold text-gray-900">{fmtINR(subtotal)}</div>
          </div>
          <div className="rounded-xl border p-3">
            <div className="text-gray-500">Discount</div>
            <div className="font-semibold text-gray-900">{fmtINR(totalDiscount)}</div>
          </div>
          <div className="rounded-xl border p-3">
            <div className="text-gray-500">GST</div>
            <div className="font-semibold text-gray-900">{fmtINR(totalGST)}</div>
          </div>
          <div className="rounded-xl border p-3 bg-gray-50">
            <div className="text-gray-700">Grand Total</div>
            <div className="text-xl font-bold">{fmtINR(total)}</div>
          </div>
        </div>
      </div>

      {/* Sticky footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur border-t shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            {orderItems.length} item{orderItems.length !== 1 ? "s" : ""} • <span className="font-semibold">{fmtINR(total)}</span>
          </div>
          <button
            onClick={handleSubmit}
            className="px-5 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            disabled={!formData.customer_id || orderItems.length === 0}
          >
            Place Order
          </button>
        </div>
      </div>
    </div>
  );
}
